import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * "Add a number" — a person in the account puts one number on Do not call by hand.
 *
 * The entry belongs to the ACCOUNT, not to the workspace this console is showing (0001: the
 * table has no `workspace_id`). "Covers" narrows which agents it stops, never which workspace
 * sees it; the panel says so, because a person working in one workspace would otherwise assume
 * a number they add here is invisible to the others.
 *
 * The note has no column. It goes into the audit entry, and the row reads it back from there
 * (customer-portal review #245), so the panel says it is shown with the number.
 *
 * AMENDED 2026-09-27 (customer-portal review #244, #248, #249, #259). The fields are a form, so
 * Enter adds; the phone field has the focus when the panel opens; a refusal clears as soon as a
 * field changes; the name and the note stop at the API's 120 and 500 characters; "Which agent"
 * offers only agents that can place calls (a failed or never-built agent cannot), telling equal
 * names apart; and the added row goes back to the screen, which says what was added.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Field, Input, Segmented, Select, type SegmentedOption } from "@wetalk/ui";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { refusalText } from "./do-not-call-copy.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY, voiceAgentListQuery } from "../../api/query.js";
import type { DoNotCallRowView, DoNotCallScopeView, VoiceAgentView } from "../../api/gateway.js";

const SCOPE_OPTION: readonly SegmentedOption<DoNotCallScopeView>[] = [
  { value: "every_voice_agent", get label() { return portalText("finance", "m0967"); } },
  { value: "one_voice_agent", get label() { return portalText("finance", "m0968"); } },
];

/** The API's own ceilings (`DoNotCallEntryCreate`), so the fields stop where it would refuse. */
const DISPLAY_NAME_MAX = 120;
const NOTE_MAX = 500;

/** `+30 210 123 4567` → `+302101234567`: spaces, dots, dashes and brackets are layout. */
function e164Of(typed: string): string {
  return typed.replace(/[\s().-]/g, "");
}

function textOrNull(typed: string): string | null {
  const trimmed = typed.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * The agents an entry for "one agent" can name: those with a published version, live or paused
 * — the only ones a campaign can dial from (review #248). A name two of them share is told apart
 * by the agent's number, or its version before it has one.
 */
export function coverableVoiceAgentOf(
  voiceAgent: readonly VoiceAgentView[],
): readonly { readonly voiceAgentId: string; readonly label: string }[] {
  const callable = voiceAgent.filter((one) => one.liveAgentVersionId !== null);
  return callable.map((one) => {
    const shared = callable.filter((other) => other.displayName === one.displayName).length > 1;
    return {
      voiceAgentId: one.voiceAgentId,
      label: shared ? `${one.displayName} (${one.partyNumber ?? one.liveAgentVersionLabel})` : one.displayName,
    };
  });
}

export function DoNotCallAddPanel({
  onClose,
  onAdded,
}: {
  readonly onClose: () => void;
  /** The entry as saved, so the screen can say what was added while the list is re-read. */
  readonly onAdded: (row: DoNotCallRowView) => void;
}): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [number, setNumber] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [scope, setScope] = useState<DoNotCallScopeView>("every_voice_agent");
  const [voiceAgentId, setVoiceAgentId] = useState("");
  const [note, setNote] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  // The phone field has the focus when the panel opens (review #259), as Contact lists does it.
  useEffect(() => {
    formRef.current?.querySelector<HTMLInputElement>("input[type=\"tel\"]")?.focus();
  }, []);

  const voiceAgent = useQuery({
    ...voiceAgentListQuery(gateway),
    enabled: scope === "one_voice_agent",
  });
  const coverable = voiceAgent.data === undefined ? undefined : coverableVoiceAgentOf(voiceAgent.data);

  const add = useMutation({
    mutationFn: () =>
      gateway.addDoNotCallEntry({
        e164: e164Of(number),
        displayName: textOrNull(displayName),
        scope,
        voiceAgentId: scope === "one_voice_agent" ? voiceAgentId : null,
        note: textOrNull(note),
      }),
    onSuccess: (row) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY.doNotCall });
      onAdded(row);
      onClose();
    },
  });

  /** A refusal is about what was sent; once a field changes it no longer describes the form. */
  const edited = (): void => {
    if (add.isError) add.reset();
  };

  const saveable =
    e164Of(number) !== "" &&
    (scope === "every_voice_agent" || voiceAgentId !== "") &&
    !add.isPending;

  return (
    <form
      ref={formRef}
      className="c-panel dnc-panel"
      aria-label={portalText("finance", "m0970")}
      onSubmit={(event) => {
        event.preventDefault();
        if (saveable) add.mutate();
      }}
    >
      <p className="dnc-panel-title">{portalText("finance", "m0970")}</p>
      <div className="dnc-panel-grid">
        <Field label={portalText("finance", "m0146")} description={portalText("finance", "m0971")}>
          {(control) => (
            <Input
              {...control}
              type="tel"
              value={number}
              placeholder="+30 210 123 4567"
              onChange={(event) => {
                setNumber(event.currentTarget.value);
                edited();
              }}
            />
          )}
        </Field>
        <Field label={portalText("finance", "m0972")}>
          {(control) => (
            <Input
              {...control}
              value={displayName}
              maxLength={DISPLAY_NAME_MAX}
              placeholder={portalText("finance", "m0973")}
              onChange={(event) => {
                setDisplayName(event.currentTarget.value);
                edited();
              }}
            />
          )}
        </Field>
        <Field
          label={portalText("finance", "m0974")}
          description={portalText("finance", "m0975")}
        >
          {() => (
            <Segmented
              name="doNotCallScope"
              legend={portalText("finance", "m0974")}
              value={scope}
              options={SCOPE_OPTION}
              onChange={(next) => {
                setScope(next);
                edited();
              }}
            />
          )}
        </Field>
        {scope === "one_voice_agent" ? (
          <Field label={portalText("finance", "m0976")}>
            {(control) =>
              voiceAgent.isError ? (
                <span className="c-note c-note-critical" role="alert">{portalText("finance", "m0977")}</span>
              ) : coverable !== undefined && coverable.length === 0 ? (
                <span className="c-note" role="status">{portalText("finance", "m0978")}</span>
              ) : (
                <Select
                  {...control}
                  value={voiceAgentId}
                  onChange={(event) => {
                    setVoiceAgentId(event.currentTarget.value);
                    edited();
                  }}
                >
                  <option value="" disabled>
                    {coverable === undefined ? portalText("finance", "m0979") : portalText("finance", "m0980")}
                  </option>
                  {(coverable ?? []).map((one) => (
                    <option key={one.voiceAgentId} value={one.voiceAgentId}>
                      {one.label}
                    </option>
                  ))}
                </Select>
              )
            }
          </Field>
        ) : null}
        <Field
          label={portalText("finance", "m0981")}
          description={portalText("finance", "m0982")}
        >
          {(control) => (
            <Input
              {...control}
              value={note}
              maxLength={NOTE_MAX}
              placeholder={portalText("finance", "m0983")}
              onChange={(event) => {
                setNote(event.currentTarget.value);
                edited();
              }}
            />
          )}
        </Field>
      </div>

      {add.isError ? (
        <p className="c-note c-note-critical" role="alert">
          {refusalText("add", add.error)}
        </p>
      ) : null}

      <div className="c-row dnc-panel-foot">
        <Button variant="secondary" onClick={onClose}>{portalText("finance", "m0418")}</Button>
        <Button variant="primary" type="submit" disabled={!saveable}>
          {add.isPending ? portalText("finance", "m0984") : portalText("finance", "m0985")}
        </Button>
      </div>
    </form>
  );
}
